import { defaultHeaders } from "@/api/http";
import { SESSION_ENDED_CODE, handleSsoSessionEnded } from "@/utils/sso-session";

/**
 * A structured failure from a mutation endpoint. `kind` is one of the flow's own
 * 409 `errorCode` values, or `generic` for everything else (network failure,
 * validation errors, unexpected status codes).
 */
export interface FlowError<TKind extends string> {
  kind: TKind | "generic";
  message: string;
  /**
   * The ProblemDetails body verbatim, so a flow can read the extra fields its own
   * 409 carries (e.g. the checkout currency conflict's `existingCurrency` /
   * `requestedCurrency`) without every caller re-parsing the response. Absent when
   * the body was missing or unparseable.
   */
  details?: Record<string, unknown>;
}

export type FlowResult<TData, TKind extends string> =
  | { ok: true; data: TData }
  | { ok: false; error: FlowError<TKind> };

interface ProblemDetails {
  detail?: string;
  errorCode?: string;
  code?: string;
  /** ValidationProblemDetails shape: field → list of error messages. */
  errors?: Record<string, string[]>;
  /** Flow-specific extensions live alongside the fields above. */
  [key: string]: unknown;
}

function summariseProblem(problem: ProblemDetails): string | undefined {
  if (problem.errors) {
    const messages = Object.values(problem.errors).flat().filter(Boolean);
    if (messages.length > 0) return messages.join(" ");
  }
  return problem.detail;
}

/**
 * POSTs JSON and, on failure, parses the ProblemDetails body into a structured
 * error. Each flow passes the `errorCode` values it knows how to reroute on
 * (`knownErrorCodes`); anything else — including a body we can't parse — comes
 * back as `generic` so the caller can fall back to a plain error message.
 *
 * Shared by every my-account flow with its own 409 vocabulary (plan change, GPS
 * MAX migration, subscription consolidation, device cleanup): they differ only in
 * that vocabulary, and three hand-copied versions of this function had already
 * started drifting in their comments.
 */
export async function postWithProblemDetails<TData, TKind extends string>(
  url: string,
  body: unknown,
  knownErrorCodes: readonly TKind[]
): Promise<FlowResult<TData, TKind>> {
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        // Site / culture / store resolution — must mirror getJson/postJson.
        ...defaultHeaders(),
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    return {
      ok: false,
      error: {
        kind: "generic",
        message: err instanceof Error ? err.message : "Network error",
      },
    };
  }

  if (response.ok) {
    if (response.status === 204) {
      return { ok: true, data: null as unknown as TData };
    }
    try {
      const data = (await response.json()) as TData;
      return { ok: true, data };
    } catch {
      return { ok: true, data: null as unknown as TData };
    }
  }

  try {
    const problem = (await response.json()) as ProblemDetails;
    // Same reaction as the shared fetch layer's: this function reads the body
    // itself, so it has to check the code itself too.
    if (response.status === 401 && problem.code === SESSION_ENDED_CODE) {
      handleSsoSessionEnded();
    }
    const kind = knownErrorCodes.find((code) => code === problem.errorCode);
    if (kind) {
      return {
        ok: false,
        error: { kind, message: problem.detail ?? kind, details: problem },
      };
    }
    const summary = summariseProblem(problem);
    return {
      ok: false,
      error: {
        kind: "generic",
        message: summary ?? `${response.status} ${response.statusText}`.trim(),
        details: problem,
      },
    };
  } catch {
    return {
      ok: false,
      error: {
        kind: "generic",
        message: `${response.status} ${response.statusText}`.trim(),
      },
    };
  }
}
